import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Input } from '../../../../catalyst-components/input';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Dialog, DialogBody, DialogTitle } from '../../../../catalyst-components/dialog';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../catalyst-components/accordion';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { AcornIcon } from '../../../../shared/components/AcornIcon';

export const AcornConfig: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);

  // Queries
  const config = useQuery(api.acorns.getConfig);
  const levelAllowances = useQuery(api.acorns.getLevelAllowances) || [];
  const skills = useQuery(api.skills.list) || [];

  // Mutations
  const upsertConfig = useMutation(api.acorns.upsertConfig);
  const seedDefaultConfig = useMutation(api.acorns.seedDefaultConfig);
  const upsertLevelAllowance = useMutation(api.acorns.upsertLevelAllowance);
  const deleteLevelAllowance = useMutation(api.acorns.deleteLevelAllowance);
  const seedDefaultLevelAllowances = useMutation(api.acorns.seedDefaultLevelAllowances);
  const upsertSkillBonus = useMutation(api.acorns.upsertSkillBonus);
  const deleteSkillBonus = useMutation(api.acorns.deleteSkillBonus);
  const seedDefaultSkillBonuses = useMutation(api.acorns.seedDefaultSkillBonuses);

  // State
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showLevelDialog, setShowLevelDialog] = useState(false);
  const [showSkillBonusDialog, setShowSkillBonusDialog] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<Id<"skills"> | null>(null);
  const [editingLevel, setEditingLevel] = useState<any>(null);
  const [editingSkillBonus, setEditingSkillBonus] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; type: 'level' | 'skill'; id?: string }>({ open: false, type: 'level' });
  const [alert, setAlert] = useState<{ open: boolean; message: string; success: boolean }>({ open: false, message: '', success: false });

  // Form state for global config
  const [configForm, setConfigForm] = useState({
    tokens_per_acorn: 200,
    base_daily_allowance: 50,
    allowance_reset_hour: 0,
    min_session_acorn_reward: 5,
    checkin_reward: 3,
    initial_balance: 0,
  });

  // Load config into form only when dialog opens, not on every Convex refresh
  // This prevents overwriting user edits mid-editing
  const openConfigDialog = () => {
    if (config) {
      setConfigForm({
        tokens_per_acorn: config.tokens_per_acorn,
        base_daily_allowance: config.base_daily_allowance,
        allowance_reset_hour: config.allowance_reset_hour,
        min_session_acorn_reward: config.min_session_acorn_reward,
        checkin_reward: config.checkin_reward ?? 3,
        initial_balance: config.initial_balance ?? 0,
      });
    }
    setShowConfigDialog(true);
  };

  // Form state for level allowance
  const [levelForm, setLevelForm] = useState({
    level: 1,
    daily_allowance: 50,
  });

  // Form state for skill bonus
  const [skillBonusForm, setSkillBonusForm] = useState({
    level: 1,
    daily_bonus: 5,
  });

  // Query all skill bonuses at once
  const allSkillBonuses = useQuery(api.acorns.getAllSkillBonuses) || [];

  // Group bonuses by skill
  const getSkillBonuses = (skillId: Id<"skills">) => {
    return allSkillBonuses
      .filter((b) => b.skill_id === skillId)
      .sort((a, b) => a.level - b.level);
  };

  const handleSaveConfig = async () => {
    try {
      await upsertConfig(configForm);
      setShowConfigDialog(false);
      setAlert({ open: true, message: 'Acorn configuration saved!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error saving configuration.', success: false });
    }
  };

  const handleSeedConfig = async () => {
    try {
      await seedDefaultConfig({ user_id: convexUserId as Id<"users"> });
      setAlert({ open: true, message: 'Default configuration created!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error creating default config.', success: false });
    }
  };

  const handleSaveLevelAllowance = async () => {
    try {
      await upsertLevelAllowance(levelForm);
      setShowLevelDialog(false);
      setEditingLevel(null);
      setAlert({ open: true, message: 'Level allowance saved!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error saving level allowance.', success: false });
    }
  };

  const handleSeedLevelAllowances = async () => {
    try {
      await seedDefaultLevelAllowances({ user_id: convexUserId as Id<"users"> });
      setAlert({ open: true, message: 'Default level allowances created!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error creating default allowances.', success: false });
    }
  };

  const handleDeleteLevelAllowance = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteLevelAllowance({ id: confirmDelete.id as Id<"acorn_level_allowance"> });
      setConfirmDelete({ open: false, type: 'level' });
      setAlert({ open: true, message: 'Level allowance deleted!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error deleting allowance.', success: false });
    }
  };

  const handleSaveSkillBonus = async () => {
    if (!selectedSkillId) return;
    try {
      await upsertSkillBonus({
        skill_id: selectedSkillId,
        level: skillBonusForm.level,
        daily_bonus: skillBonusForm.daily_bonus,
      });
      setShowSkillBonusDialog(false);
      setEditingSkillBonus(null);
      setAlert({ open: true, message: 'Skill bonus saved!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error saving skill bonus.', success: false });
    }
  };

  const handleDeleteSkillBonus = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteSkillBonus({ id: confirmDelete.id as Id<"acorn_skill_bonus"> });
      setConfirmDelete({ open: false, type: 'skill' });
      setAlert({ open: true, message: 'Skill bonus deleted!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error deleting skill bonus.', success: false });
    }
  };

  const handleSeedSkillBonuses = async () => {
    try {
      await seedDefaultSkillBonuses({ user_id: convexUserId as Id<"users"> });
      setAlert({ open: true, message: 'Default skill bonuses created for all skills!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error creating default skill bonuses.', success: false });
    }
  };

  const openEditLevel = (level: any) => {
    setEditingLevel(level);
    setLevelForm({
      level: level.level,
      daily_allowance: level.daily_allowance,
    });
    setShowLevelDialog(true);
  };

  const openAddLevel = () => {
    setEditingLevel(null);
    const nextLevel = levelAllowances.length > 0 ? Math.max(...levelAllowances.map(l => l.level)) + 1 : 1;
    setLevelForm({
      level: nextLevel,
      daily_allowance: 50,
    });
    setShowLevelDialog(true);
  };

  const openEditSkillBonus = (skillId: Id<"skills">, bonus: any) => {
    setSelectedSkillId(skillId);
    setEditingSkillBonus(bonus);
    setSkillBonusForm({
      level: bonus.level,
      daily_bonus: bonus.daily_bonus,
    });
    setShowSkillBonusDialog(true);
  };

  const openAddSkillBonus = (skillId: Id<"skills">, existingBonuses: any[]) => {
    setSelectedSkillId(skillId);
    setEditingSkillBonus(null);
    const nextLevel = existingBonuses.length > 0 ? Math.max(...existingBonuses.map(b => b.level)) + 1 : 1;
    setSkillBonusForm({
      level: nextLevel,
      daily_bonus: 5,
    });
    setShowSkillBonusDialog(true);
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-accent/10 to-accent/5 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button
              color="secondary"
              className="rounded-full"
              onClick={() => navigate('../')}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <AcornIcon size={48} className="text-primary" />
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">Acorn Economy Configuration</h1>
              <p className="text-base text-muted-foreground">
                Configure the acorn currency system: token-to-acorn conversion, daily allowances, and rewards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-8">
          {/* Global Configuration */}
          <div className="border-2 border-accent/30 bg-card p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Global Settings</h2>
                <p className="text-sm text-muted-foreground mt-1">Core configuration for the acorn economy</p>
              </div>
              <div className="flex gap-2">
                {!config && (
                  <Button color="secondary" className="rounded-full" onClick={handleSeedConfig}>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Create Default Config
                  </Button>
                )}
                <Button color="primary" className="rounded-full" onClick={openConfigDialog}>
                  <PencilIcon className="h-4 w-4 mr-2" />
                  {config ? 'Edit Settings' : 'Configure'}
                </Button>
              </div>
            </div>

            {config ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">Tokens per Acorn</div>
                  <div className="text-2xl font-semibold text-foreground">{config.tokens_per_acorn}</div>
                  <div className="text-xs text-muted-foreground mt-1">AI tokens consumed per 1 acorn cost</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">Base Daily Allowance</div>
                  <div className="text-2xl font-semibold text-foreground flex items-center gap-1.5">{config.base_daily_allowance} <AcornIcon size={22} className="text-primary inline-block" /></div>
                  <div className="text-xs text-muted-foreground mt-1">Default daily acorns before level bonuses</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">Daily Allowance Reset</div>
                  <div className="text-2xl font-semibold text-foreground">Local Midnight</div>
                  <div className="text-xs text-muted-foreground mt-1">Resets at midnight in each user's local timezone (not UTC)</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">Min Session Reward</div>
                  <div className="text-2xl font-semibold text-foreground flex items-center gap-1.5">{config.min_session_acorn_reward} <AcornIcon size={22} className="text-primary inline-block" /></div>
                  <div className="text-xs text-muted-foreground mt-1">Default reward if not set on assistant</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">Check-in Reward</div>
                  <div className="text-2xl font-semibold text-foreground flex items-center gap-1.5">{config.checkin_reward ?? 3} <AcornIcon size={22} className="text-primary inline-block" /></div>
                  <div className="text-xs text-muted-foreground mt-1">Acorns granted for completing a check-in</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                  <div className="text-sm text-muted-foreground">New User Starting Balance</div>
                  <div className="text-2xl font-semibold text-foreground flex items-center gap-1.5">{config.initial_balance ?? 0} <AcornIcon size={22} className="text-primary inline-block" /></div>
                  <div className="text-xs text-muted-foreground mt-1">Acorns granted to new users on first activity</div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8 bg-muted/30 rounded-xl">
                No configuration found. Click "Create Default Config" to get started.
              </p>
            )}
          </div>

          {/* Level-Based Daily Allowance */}
          <div className="border-2 border-primary/30 bg-card p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Level-Based Daily Allowance</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Users receive daily acorns based on their overall level. Higher levels = more daily acorns.
                </p>
              </div>
              <div className="flex gap-2">
                {levelAllowances.length === 0 && (
                  <Button color="secondary" className="rounded-full" onClick={handleSeedLevelAllowances}>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Create Default
                  </Button>
                )}
                <Button color="primary" className="rounded-full" onClick={openAddLevel}>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Level
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border/50 overflow-hidden">
              <Table>
                <TableHead>
                  <TableRow className="bg-muted/30">
                    <TableHeader>Level</TableHeader>
                    <TableHeader>Daily Allowance</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {levelAllowances.map((level) => (
                    <TableRow key={level._id} className="hover:bg-muted/20">
                      <TableCell className="font-medium">Level {level.level}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1">{level.daily_allowance} <AcornIcon size={16} className="text-primary" /></span></TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button color="secondary" className="rounded-full" onClick={() => openEditLevel(level)}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            color="red"
                            className="rounded-full"
                            onClick={() => setConfirmDelete({ open: true, type: 'level', id: level._id })}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Skill-Based Bonus Allowance */}
          <div className="border-2 border-purple-200 bg-card p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Skill-Based Bonus Allowance</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Users get bonus daily acorns for each skill level they've achieved. These stack with the overall level allowance.
                </p>
              </div>
              <Button color="secondary" className="rounded-full" onClick={handleSeedSkillBonuses}>
                <SparklesIcon className="h-4 w-4 mr-2" />
                Seed All Default Bonuses
              </Button>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {skills.map((skill) => {
                const bonuses = getSkillBonuses(skill._id);
                return (
                  <AccordionItem
                    key={skill._id}
                    value={skill._id}
                    className="border-2 border-border/50 rounded-xl overflow-hidden bg-gradient-to-br from-purple-50/50 to-purple-100/30"
                  >
                    <AccordionTrigger className="text-xl font-semibold px-6 py-4 hover:bg-purple-50/50">
                      <span className="flex items-center gap-3">
                        {skill.name}
                        <span className="text-sm font-normal text-muted-foreground">
                          ({bonuses.length} bonus levels)
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent value={skill._id}>
                      <div className="px-6 pb-6 space-y-4">
                        <div className="flex justify-end gap-2">
                          <Button color="primary" className="rounded-full" onClick={() => openAddSkillBonus(skill._id, bonuses)}>
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Bonus Level
                          </Button>
                        </div>

                        {bonuses.length > 0 ? (
                          <div className="rounded-xl border border-border/50 overflow-hidden">
                            <Table>
                              <TableHead>
                                <TableRow className="bg-muted/30">
                                  <TableHeader>Skill Level</TableHeader>
                                  <TableHeader>Daily Bonus</TableHeader>
                                  <TableHeader>Actions</TableHeader>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {bonuses.map((bonus: any) => (
                                  <TableRow key={bonus._id} className="hover:bg-muted/20">
                                    <TableCell className="font-medium">Level {bonus.level}</TableCell>
                                    <TableCell className="text-primary font-semibold"><span className="inline-flex items-center gap-1">+{bonus.daily_bonus} <AcornIcon size={16} className="text-primary" /></span></TableCell>
                                    <TableCell>
                                      <div className="flex gap-2">
                                        <Button color="secondary" className="rounded-full" onClick={() => openEditSkillBonus(skill._id, bonus)}>
                                          <PencilIcon className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          color="red"
                                          className="rounded-full"
                                          onClick={() => setConfirmDelete({ open: true, type: 'skill', id: bonus._id })}
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-center py-8 bg-muted/30 rounded-xl">
                            No bonus levels configured for this skill yet.
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </div>

      {/* Global Config Dialog */}
      <Dialog open={showConfigDialog} onClose={() => setShowConfigDialog(false)}>
        <DialogBody>
          <DialogTitle>Acorn Economy Settings</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Tokens per Acorn</Label>
              <Input
                type="number"
                min="1"
                value={configForm.tokens_per_acorn}
                onChange={(e) => setConfigForm({ ...configForm, tokens_per_acorn: parseInt(e.target.value) || 200 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                How many AI tokens equal 1 acorn cost. Lower = more expensive chat.
              </p>
            </Field>
            <Field>
              <Label>Base Daily Allowance</Label>
              <Input
                type="number"
                min="0"
                value={configForm.base_daily_allowance}
                onChange={(e) => setConfigForm({ ...configForm, base_daily_allowance: parseInt(e.target.value) || 50 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Default daily acorns before level-based bonuses.
              </p>
            </Field>
            <div className="p-3 bg-muted/40 rounded-lg border border-border/50 text-xs text-muted-foreground">
              Daily allowance resets at midnight in each user's local timezone.
            </div>
            <Field>
              <Label>Minimum Session Acorn Reward</Label>
              <Input
                type="number"
                min="0"
                value={configForm.min_session_acorn_reward}
                onChange={(e) => setConfigForm({ ...configForm, min_session_acorn_reward: parseInt(e.target.value) || 5 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Default reward if assistant doesn't have specific reward set.
              </p>
            </Field>
            <Field>
              <Label>Check-in Reward</Label>
              <Input
                type="number"
                min="0"
                value={configForm.checkin_reward}
                onChange={(e) => setConfigForm({ ...configForm, checkin_reward: parseInt(e.target.value) || 3 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Acorns granted when a user completes a check-in.
              </p>
            </Field>
            <Field>
              <Label>New User Starting Balance</Label>
              <Input
                type="number"
                min="0"
                value={configForm.initial_balance}
                onChange={(e) => setConfigForm({ ...configForm, initial_balance: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Acorns given to new users when their balance is first created.
              </p>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button color="light" onClick={handleSaveConfig}>
              Save
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Level Allowance Dialog */}
      <Dialog open={showLevelDialog} onClose={() => setShowLevelDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingLevel ? 'Edit Level Allowance' : 'Add Level Allowance'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Level Number</Label>
              <Input
                type="number"
                min="1"
                value={levelForm.level}
                onChange={(e) => setLevelForm({ ...levelForm, level: parseInt(e.target.value) || 1 })}
              />
            </Field>
            <Field>
              <Label>Daily Allowance</Label>
              <Input
                type="number"
                min="0"
                value={levelForm.daily_allowance}
                onChange={(e) => setLevelForm({ ...levelForm, daily_allowance: parseInt(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowLevelDialog(false)}>
              Cancel
            </Button>
            <Button color="light" onClick={handleSaveLevelAllowance}>
              Save
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Skill Bonus Dialog */}
      <Dialog open={showSkillBonusDialog} onClose={() => setShowSkillBonusDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingSkillBonus ? 'Edit Skill Bonus' : 'Add Skill Bonus'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Skill Level</Label>
              <Input
                type="number"
                min="1"
                value={skillBonusForm.level}
                onChange={(e) => setSkillBonusForm({ ...skillBonusForm, level: parseInt(e.target.value) || 1 })}
              />
            </Field>
            <Field>
              <Label>Daily Bonus</Label>
              <Input
                type="number"
                min="0"
                value={skillBonusForm.daily_bonus}
                onChange={(e) => setSkillBonusForm({ ...skillBonusForm, daily_bonus: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Extra daily acorns for achieving this skill level.
              </p>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowSkillBonusDialog(false)}>
              Cancel
            </Button>
            <Button color="light" onClick={handleSaveSkillBonus}>
              Save
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, type: 'level' })}
        onConfirm={confirmDelete.type === 'level' ? handleDeleteLevelAllowance : handleDeleteSkillBonus}
        title="Delete Configuration"
        description="Are you sure you want to delete this configuration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />

      {/* Alert */}
      <Alert open={alert.open} onClose={() => setAlert({ ...alert, open: false })} size="lg">
        <AlertTitle>{alert.success ? 'Success' : 'Error'}</AlertTitle>
        <AlertDescription>{alert.message}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setAlert({ ...alert, open: false })}>
            OK
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
